/**
 * Registry of OmicsBox cloud runs that are not yet complete. Correlation is by `run_id`.
 *
 * Two kinds of run share this module, distinguished by {@link PendingRun.source}:
 *  - `chat`: submitted by the agent, learned from a turn's RUN_FINISHED `pending_runs`, and tied to
 *    the conversation that should auto-continue when it finishes;
 *  - `viewer`: launched by the user from a viewer action wizard (`JobLaunchHost`), with no
 *    conversation behind it.
 *
 * They share one registry because they share the resolution path: a single `job.status`
 * notification carries only a `run_id`, so whatever receives it has to look the id up without
 * knowing which feature started the run.
 *
 * Chat resolution is two steps — `resolvePendingRun` (it finished) and `removePendingRun` (drop it
 * from the strip). `SocketProvider` is always mounted, but the chat panel is not, and the
 * conversation's session controller only exists while it is an open tab. Marking and removing
 * separately means a completion that arrives with the panel closed waits here instead of being
 * thrown away, and the continuation fires when the panel comes back.
 *
 * Viewer runs have no follow-up turn, so `resolvePendingRun` removes them immediately.
 *
 * The server owns the chat list (`GET /pending-runs`); localStorage is only a first-paint cache.
 * Viewer runs are local-only — the chat registry has never heard of them — and hydration must not
 * drop them as "unconfirmed".
 */

import type {TransferOutcome} from './transferContinuation'
import type {JobStatus} from './jobStatus'

/** Same set of terminal states the `job.status` notification carries — kept as one definition. */
export type PendingRunStatus = JobStatus

export interface PendingRunResolution {
  status: PendingRunStatus
  /** epoch ms the notification arrived */
  at: number
  /** Set for a chat transfer (`import.status`): its own outcome, for a transfer-worded follow-up. */
  transfer?: TransferOutcome
}

/** Where a pending run came from, which decides who is allowed to resolve it. */
export type PendingRunSource = 'chat' | 'viewer'

export interface PendingRun {
  runId: string
  tool?: string
  state?: string
  /** Human-readable job name for the viewer indicator; falls back to `tool`. */
  label?: string
  /**
   * Defaults to `chat` for entries persisted before this field existed — those were all
   * agent-submitted and all carried a conversationId.
   */
  source?: PendingRunSource
  /** The conversation to auto-continue. Unset for `viewer` runs. */
  conversationId?: string
  /** epoch ms the run was registered; drives the GC below */
  createdAt: number
  /** Set once the run reached a terminal state. Present = waiting for its follow-up turn. */
  resolution?: PendingRunResolution
  /**
   * `server` — the registry knows this run, so its continuation must be claimed before dispatch.
   * `local` — this browser only; continue without claiming (there is no row to claim).
   */
  origin?: 'server' | 'local'
}

/** One row of `GET /pending-runs`, in the shape this store consumes. */
export interface ServerPendingRun {
  runId: string
  conversationId: string
  tool?: string | null
  state?: string
  resolvedAt?: string | null
  status?: string | null
  createdAt?: string
}

const STORAGE_KEY = 'omicsai-pending-runs'
/**
 * How long a run this browser believes in may go unconfirmed by the server before it is dropped.
 *
 * Optimistic rows exist so a tag appears the instant a turn says it launched something. But an id
 * the server never confirms is almost certainly not a run at all — the id came from a model reading
 * its own prose — and keeping it forever produces a tag nothing can ever resolve. Seen in
 * production: "Running omicsbox_run_tool", which is a tool name, not a run.
 */
const UNCONFIRMED_MAX_AGE_MS = 10 * 60 * 1000
/** Runs older than this are dropped on load: a run we never resolved is not worth a permanent tag. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
/** Bound the registry so abandoned viewer runs (nothing polls them yet) cannot grow without limit. */
const MAX_RUNS = 50

function sourceOf(run: PendingRun): PendingRunSource {
  return run.source === 'viewer' ? 'viewer' : 'chat'
}

type Listener = () => void
const listeners = new Set<Listener>()

// runId -> PendingRun (single source of truth; conversation grouping is derived).
const runs = new Map<string, PendingRun>()

function readStorage(): PendingRun[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const arr = JSON.parse(raw) as PendingRun[]
    return Array.isArray(arr) ? arr : []
  } catch {
    return [] /* corrupt/absent storage */
  }
}

/** Drop the oldest entries once the registry is past MAX_RUNS. Returns whether anything was removed. */
function prune(map: Map<string, PendingRun>): boolean {
  if (map.size <= MAX_RUNS) return false
  const dropCount = map.size - MAX_RUNS
  const oldest = [...map.values()].sort((a, b) => a.createdAt - b.createdAt).slice(0, dropCount)
  oldest.forEach((r) => map.delete(r.runId))
  return true
}

/** Replace the in-memory map from a stored snapshot, dropping anything past MAX_AGE_MS. */
function loadInto(map: Map<string, PendingRun>, snapshot: PendingRun[]): void {
  const cutoff = Date.now() - MAX_AGE_MS
  map.clear()
  for (const r of snapshot) {
    if (!r?.runId) continue
    const source = sourceOf(r)
    if (source === 'chat' && !r.conversationId) continue
    // A row with no createdAt predates this field, so treat it as ancient rather than new. Dating
    // it "now" on every load made such a row immortal: forever inside both the GC window and the
    // unconfirmed cutoff, so its tag could never age out.
    const createdAt = typeof r.createdAt === 'number' ? r.createdAt : 0
    if (createdAt < cutoff) continue
    map.set(r.runId, {...r, source, createdAt})
  }
  prune(map)
}

loadInto(runs, readStorage())

function persist(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...runs.values()]))
  } catch {
    /* ignore quota/private-mode */
  }
}

function notify(): void {
  persist()
  listeners.forEach((l) => {
    try {
      l()
    } catch {
      /* a listener throwing must not break the others */
    }
  })
}

// Another tab changed the registry (registered a run, resolved one, dismissed one). `storage` only
// fires in *other* tabs, so adopting the snapshot wholesale can't loop — and last writer wins, which
// is the right call for a store the server will own shortly.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY) return
    loadInto(runs, readStorage())
    listeners.forEach((l) => {
      try {
        l()
      } catch {
        /* as above */
      }
    })
  })
}

/** Record the runs the agent just submitted (from RUN_FINISHED `pending_runs`) against a conversation. */
export function registerPendingRuns(
  conversationId: string,
  incoming: Array<{run_id?: string; runId?: string; tool?: string; state?: string}> | undefined,
): void {
  if (!conversationId || !incoming?.length) return
  let changed = false
  for (const r of incoming) {
    const runId = String(r?.run_id ?? r?.runId ?? '').trim()
    if (!runId || runs.has(runId)) continue
    // Optimistic: the middleware wrote the real row earlier in this same turn, so the next fetch
    // replaces this with the server's version.
    runs.set(runId, {
      runId,
      tool: r.tool,
      state: r.state,
      source: 'chat',
      conversationId,
      createdAt: Date.now(),
      origin: 'local',
    })
    changed = true
  }
  if (prune(runs)) changed = true
  if (changed) notify()
}

/**
 * Record a run launched from a viewer action wizard.
 *
 * No conversationId: there is no chat turn to continue, so resolution only clears the indicator.
 */
export function registerViewerRun(run: {
  runId: string
  tool?: string
  state?: string
  label?: string
}): void {
  const runId = String(run?.runId ?? '').trim()
  if (!runId || runs.has(runId)) return
  runs.set(runId, {
    runId,
    tool: run.tool,
    state: run.state,
    label: run.label,
    source: 'viewer',
    createdAt: Date.now(),
    origin: 'local',
  })
  prune(runs)
  notify()
}

/**
 * Look a run up without removing it.
 *
 * Needed because a `job.status` notification is broadcast to every subscriber: a consumer must be
 * able to check whether a run is *its own* before consuming it, or the first subscriber to see the
 * event would swallow another feature's run.
 */
export function getPendingRun(runId: string): PendingRun | null {
  return runs.get(String(runId ?? '').trim()) ?? null
}

/**
 * Adopt the server's list. Server rows replace whatever this browser believed; local-only runs the
 * server has never heard of are preserved (see the note at the top). Subscribers are notified only
 * if something actually changed, so a poll that finds nothing new doesn't re-render the composer.
 */
export function hydrateFromServer(rows: ServerPendingRun[]): void {
  const next = new Map<string, PendingRun>()

  for (const row of rows || []) {
    const runId = String(row?.runId ?? '').trim()
    const conversationId = String(row?.conversationId ?? '').trim()
    if (!runId || !conversationId) continue
    const resolvedAt = row.resolvedAt ? Date.parse(row.resolvedAt) : NaN
    next.set(runId, {
      runId,
      conversationId,
      source: 'chat',
      tool: row.tool || undefined,
      state: row.state || undefined,
      createdAt: (row.createdAt ? Date.parse(row.createdAt) : NaN) || Date.now(),
      origin: 'server',
      ...(row.resolvedAt
        ? {
            resolution: {
              status: normalizeStatus(row.status),
              at: Number.isNaN(resolvedAt) ? Date.now() : resolvedAt,
            },
          }
        : {}),
    })
  }

  const unconfirmedCutoff = Date.now() - UNCONFIRMED_MAX_AGE_MS
  for (const [runId, entry] of runs) {
    if (next.has(runId)) continue
    // Viewer runs are never in the chat registry; a poll must not treat them as unconfirmed chat ids.
    if (sourceOf(entry) === 'viewer') {
      next.set(runId, entry)
      continue
    }
    // A server row that has vanished is finished and reported; only local-only runs survive.
    if (entry.origin === 'server') continue
    // …and only briefly. The registry is authoritative, so a row it has never heard of is a
    // mis-read id, not a run — dropping it is what stops an unresolvable tag lasting for days.
    if (entry.createdAt < unconfirmedCutoff) continue
    next.set(runId, {...entry, origin: 'local'})
  }

  if (sameRuns(runs, next)) return
  runs.clear()
  for (const [runId, entry] of next) runs.set(runId, entry)
  notify()
}

function normalizeStatus(status: string | null | undefined): PendingRunStatus {
  const s = String(status ?? '').toLowerCase()
  return s === 'failed' || s === 'canceled' ? s : 'complete'
}

function sameRuns(a: Map<string, PendingRun>, b: Map<string, PendingRun>): boolean {
  if (a.size !== b.size) return false
  for (const [runId, left] of a) {
    const right = b.get(runId)
    if (!right) return false
    if (
      left.conversationId !== right.conversationId ||
      sourceOf(left) !== sourceOf(right) ||
      left.tool !== right.tool ||
      left.state !== right.state ||
      left.label !== right.label ||
      left.origin !== right.origin ||
      left.resolution?.status !== right.resolution?.status
    ) {
      return false
    }
  }
  return true
}

/**
 * Mark a run as finished (from a `job.status` notification).
 *
 * Chat runs stay until their follow-up turn is dispatched — see `getResolvedRuns`. Viewer runs have
 * no follow-up turn, so they are removed immediately.
 */
export function resolvePendingRun(
  runId: string,
  status: PendingRunStatus = 'complete',
  transfer?: TransferOutcome
): PendingRun | null {
  const id = String(runId ?? '').trim()
  const entry = runs.get(id)
  if (!entry) return null
  if (sourceOf(entry) === 'viewer') {
    runs.delete(id)
    notify()
    return entry
  }
  if (entry.resolution?.status === status) return entry
  const resolved: PendingRun = {
    ...entry,
    resolution: {status, at: Date.now(), ...(transfer ? {transfer} : {})},
  }
  runs.set(id, resolved)
  notify()
  return resolved
}

/** Drop a run from the registry — its continuation was dispatched, or the user dismissed the tag. */
export function removePendingRun(runId: string): PendingRun | null {
  const id = String(runId ?? '').trim()
  const entry = runs.get(id)
  if (!entry) return null
  runs.delete(id)
  notify()
  return entry
}

/**
 * Remove a run at the user's request. Same effect as {@link removePendingRun}, named separately
 * because it means something different: the run was not observed to finish, the user just stopped
 * wanting to see it.
 */
export function dismissPendingRun(runId: string): void {
  removePendingRun(runId)
}

/** Runs that finished and are still waiting for their follow-up turn, oldest first. */
export function getResolvedRuns(): PendingRun[] {
  return [...runs.values()]
    .filter((r) => r.resolution && sourceOf(r) !== 'viewer')
    .sort((a, b) => (a.resolution?.at ?? 0) - (b.resolution?.at ?? 0))
}

/** Whether any chat run is tracked — drives the reconcile poll. Viewer runs are local-only. */
export function hasTrackedRuns(): boolean {
  for (const r of runs.values()) {
    if (sourceOf(r) !== 'viewer') return true
  }
  return false
}

/** Current pending chat runs for a conversation (for the composer strip). */
export function getPendingRuns(conversationId: string | null | undefined): PendingRun[] {
  if (!conversationId) return []
  return [...runs.values()].filter(
    (r) => sourceOf(r) === 'chat' && r.conversationId === conversationId,
  )
}

/** Viewer-launched runs, newest first. */
export function getViewerPendingRuns(): PendingRun[] {
  return [...runs.values()]
    .filter((r) => sourceOf(r) === 'viewer')
    .sort((a, b) => b.createdAt - a.createdAt)
}

const reconcileListeners = new Set<Listener>()

/**
 * Ask whoever owns the fetch to reconcile now.
 *
 * A turn that just launched a run should show its tag immediately, not on the next 60 s poll. Asking
 * the server (rather than trusting the turn's own account of what it launched) keeps the model out
 * of the tag path entirely, which is the whole point of the registry.
 */
export function requestPendingRunsReconcile(): void {
  reconcileListeners.forEach((l) => {
    try {
      l()
    } catch {
      /* a listener throwing must not break the others */
    }
  })
}

export function subscribePendingRunsReconcile(cb: Listener): () => void {
  reconcileListeners.add(cb)
  return () => reconcileListeners.delete(cb)
}

export function subscribePendingRuns(cb: Listener): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}
